package org.example.project.planner.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.client.model.BlockType
import org.example.project.planner.BlockDraft
import org.example.project.ui.components.AppFilterChip
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.LocalPlayfulShapes
import org.example.project.ui.theme.PlayfulColors

@OptIn(ExperimentalLayoutApi::class)
@Composable
fun BlockEditorDialog(
    draft: BlockDraft,
    isNewBlock: Boolean = false,
    onDraftChange: (BlockDraft) -> Unit,
    onSave: () -> Unit,
    onDismiss: () -> Unit,
    onOpenFavoritesPicker: () -> Unit
) {
    val shapes = LocalPlayfulShapes.current

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Text(
                text = if (isNewBlock) "Nowy punkt programu" else "Edycja punktu programu",
                fontSize = 18.sp,
                fontWeight = FontWeight.Bold,
                color = PlayfulColors.TextPrimary
            )
        },
        text = {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
            ) {
                Text(
                    text = "Typ bloku:",
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                    color = PlayfulColors.TextSecondary
                )
                Spacer(modifier = Modifier.height(6.dp))

                FlowRow(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalArrangement = Arrangement.spacedBy(6.dp)
                ) {
                    BlockType.entries.forEach { bType ->
                        val label = when (bType) {
                            BlockType.CATALOG_GAME -> "Zabawa z katalogu"
                            BlockType.CUSTOM_GAME -> "Własna zabawa"
                            BlockType.OTHER -> "Inny punkt programu"
                        }
                        AppFilterChip(
                            label = label,
                            selected = draft.blockType == bType,
                            onClick = {
                                val actId = if (bType == BlockType.CATALOG_GAME) draft.activityId else null
                                onDraftChange(draft.copy(blockType = bType, activityId = actId))
                            }
                        )
                    }
                }

                if (draft.blockType == BlockType.CATALOG_GAME) {
                    Spacer(modifier = Modifier.height(12.dp))
                    OutlinedButton(
                        onClick = onOpenFavoritesPicker,
                        modifier = Modifier.fillMaxWidth(),
                        shape = shapes.button,
                        border = BorderStroke(1.dp, PlayfulColors.SunYellowBorder),
                        colors = ButtonDefaults.outlinedButtonColors(
                            containerColor = PlayfulColors.SunYellowLight,
                            contentColor = PlayfulColors.SunYellowDark
                        )
                    ) {
                        Icon(
                            imageVector = AppIcons.StarFilled,
                            contentDescription = null,
                            tint = PlayfulColors.SunYellowDark,
                            modifier = Modifier.size(16.dp)
                        )
                        Spacer(modifier = Modifier.width(6.dp))
                        Text(
                            text = "Wybierz z ulubionych zabaw",
                            fontWeight = FontWeight.SemiBold,
                            fontSize = 13.sp
                        )
                    }
                }

                Spacer(modifier = Modifier.height(12.dp))

                OutlinedTextField(
                    value = draft.title,
                    onValueChange = { onDraftChange(draft.copy(title = it)) },
                    label = { Text("Tytuł bloku") },
                    placeholder = { Text("np. Rozgrzewka z chustą") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    shape = shapes.input,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = PlayfulColors.Coral,
                        unfocusedBorderColor = PlayfulColors.Border
                    )
                )

                Spacer(modifier = Modifier.height(10.dp))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    OutlinedTextField(
                        value = draft.startTime,
                        onValueChange = { onDraftChange(draft.copy(startTime = it)) },
                        label = { Text("Start (GG:mm)", fontSize = 11.sp) },
                        placeholder = { Text("10:00", fontSize = 12.sp) },
                        modifier = Modifier.weight(1f),
                        singleLine = true,
                        shape = shapes.input,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = PlayfulColors.Coral,
                            unfocusedBorderColor = PlayfulColors.Border
                        )
                    )
                    OutlinedTextField(
                        value = draft.endTime,
                        onValueChange = { onDraftChange(draft.copy(endTime = it)) },
                        label = { Text("Koniec (GG:mm)", fontSize = 11.sp) },
                        placeholder = { Text("10:30", fontSize = 12.sp) },
                        modifier = Modifier.weight(1f),
                        singleLine = true,
                        shape = shapes.input,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = PlayfulColors.Coral,
                            unfocusedBorderColor = PlayfulColors.Border
                        )
                    )
                }

                if (draft.blockType == BlockType.CATALOG_GAME && draft.instructions.isNotBlank()) {
                    Spacer(modifier = Modifier.height(10.dp))
                    Surface(
                        color = PlayfulColors.TealLight,
                        shape = shapes.cardSmall,
                        border = BorderStroke(1.dp, PlayfulColors.TealBorder),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Column(modifier = Modifier.padding(12.dp)) {
                            Text(
                                text = "Instrukcje z katalogu (snapshot):",
                                fontSize = 11.sp,
                                color = PlayfulColors.TealDark,
                                fontWeight = FontWeight.Bold
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            Text(
                                text = draft.instructions,
                                fontSize = 12.sp,
                                lineHeight = 16.sp,
                                color = PlayfulColors.TextPrimary
                            )
                        }
                    }
                }

                if (draft.blockType != BlockType.CATALOG_GAME) {
                    Spacer(modifier = Modifier.height(10.dp))
                    OutlinedTextField(
                        value = draft.instructions,
                        onValueChange = { onDraftChange(draft.copy(instructions = it)) },
                        label = { Text("Instrukcje zabawy") },
                        modifier = Modifier.fillMaxWidth(),
                        minLines = 2,
                        shape = shapes.input,
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = PlayfulColors.Coral,
                            unfocusedBorderColor = PlayfulColors.Border
                        )
                    )
                }

                Spacer(modifier = Modifier.height(10.dp))
                OutlinedTextField(
                    value = draft.description,
                    onValueChange = { onDraftChange(draft.copy(description = it)) },
                    label = { Text("Opis bloku / przebiegu") },
                    placeholder = { Text("Opcjonalny opis przebiegu...") },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = 2,
                    shape = shapes.input,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = PlayfulColors.Coral,
                        unfocusedBorderColor = PlayfulColors.Border
                    )
                )

                Spacer(modifier = Modifier.height(10.dp))
                OutlinedTextField(
                    value = draft.notes,
                    onValueChange = { onDraftChange(draft.copy(notes = it)) },
                    label = { Text("Notatki organizatora") },
                    placeholder = { Text("np. rekwizyty, wskazówki...") },
                    modifier = Modifier.fillMaxWidth(),
                    minLines = 2,
                    shape = shapes.input,
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = PlayfulColors.Coral,
                        unfocusedBorderColor = PlayfulColors.Border
                    )
                )
            }
        },
        confirmButton = {
            Button(
                onClick = onSave,
                enabled = draft.title.isNotBlank(),
                shape = shapes.buttonSmall,
                colors = ButtonDefaults.buttonColors(
                    containerColor = PlayfulColors.Coral,
                    contentColor = PlayfulColors.TextOnPrimary
                )
            ) {
                Text(
                    text = if (isNewBlock) "Dodaj punkt" else "Zatwierdź zmiany",
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold
                )
            }
        },
        dismissButton = {
            TextButton(
                onClick = onDismiss,
                shape = shapes.buttonSmall
            ) {
                Text("Anuluj", fontSize = 13.sp, color = PlayfulColors.TextSecondary)
            }
        },
        shape = shapes.dialog,
        containerColor = PlayfulColors.Surface
    )
}
